import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faSave, faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../Api';

const PresenceChecklist = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [formation, setFormation] = useState(null);
  const [checklist, setChecklist] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [presences, setPresences] = useState({});

  useEffect(() => {
    fetchChecklist();
  }, [id]);

  const fetchChecklist = async () => {
    try {
      const response = await api.get(`/formations/${id}/presence/checklist`);
      setFormation(response.data.data.formation);
      const items = response.data.data.checklist || [];
      setChecklist(items);
      
      // Initialize presences based on status
      const initialPresences = {};
      items.forEach(item => {
        initialPresences[`${item.person_type}_${item.person_id}`] = item.status === 'completed';
      });
      setPresences(initialPresences);
    } catch (error) {
      console.error('Error fetching checklist:', error);
      setError('Erreur lors du chargement de la liste de présence');
    } finally {
      setFetching(false);
    }
  };

  const handleTogglePresence = (personType, personId) => {
    const key = `${personType}_${personId}`;
    setPresences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSelectAll = () => {
    const allPresences = {};
    checklist.forEach(item => {
      allPresences[`${item.person_type}_${item.person_id}`] = true;
    });
    setPresences(allPresences);
  };

  const handleDeselectAll = () => {
    setPresences({});
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const presenceArray = Object.entries(presences).map(([key, present]) => {
        const [personType, personId] = key.split('_');
        return {
          person_type: personType,
          person_id: parseInt(personId),
          present: present
        };
      });

      await api.post(`/formations/${id}/presence/mark`, {
        presences: presenceArray
      });

      setSuccess('Présences enregistrées avec succès');
      setTimeout(() => {
        navigate(`/formations/${id}`);
      }, 1500);
    } catch (error) {
      console.error('Error saving presence:', error);
      setError(error.response?.data?.message || 'Erreur lors de l\'enregistrement');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status, displayStatus) => {
    // Use display_status if available, otherwise use status
    const statusToCheck = displayStatus || status;
    switch (statusToCheck) {
      case 'completed_online': return 'text-red-600 bg-red-100';
      case 'completed': return 'text-green-600 bg-green-100';
      case 'in_progress': return 'text-yellow-600 bg-yellow-100';
      case 'absent': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusText = (status, displayStatus) => {
    // Use display_status if available, otherwise use status
    const statusToCheck = displayStatus || status;
    switch (statusToCheck) {
      case 'completed_online': return 'Complété en ligne';
      case 'completed': return 'Complété';
      case 'in_progress': return 'En cours';
      case 'absent': return 'Absent';
      default: return 'En attente';
    }
  };

  if (fetching) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      {/* Dotted Background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}></div>
      </div>
      
      <div className="relative z-10 p-3 sm:p-4 md:p-6 animate-slide-up">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 sm:mb-5 md:mb-6 gap-4">
            <div className="flex-1 min-w-0">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 break-words">
                LISTE DE PRÉSENCE
              </h1>
              <p className="text-xs sm:text-sm text-gray-600 mt-1 break-words">{formation?.title}</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <button
                onClick={handleSelectAll}
                className="w-full sm:w-auto px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-xs sm:text-sm font-medium"
              >
                Tout sélectionner
              </button>
              <button
                onClick={handleDeselectAll}
                className="w-full sm:w-auto px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-xs sm:text-sm font-medium"
              >
                Tout désélectionner
              </button>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
              {success}
            </div>
          )}

          {/* Checklist Table - Desktop */}
          <div className="hidden md:block bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 overflow-hidden mb-4 sm:mb-5 md:mb-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <input
                        type="checkbox"
                        checked={checklist.length > 0 && checklist.every(item => presences[`${item.person_type}_${item.person_id}`])}
                        onChange={(e) => e.target.checked ? handleSelectAll() : handleDeselectAll()}
                        className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                      />
                    </th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nom
                    </th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Assigné le
                    </th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Progression
                    </th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Statut
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {checklist.map((item) => {
                    const key = `${item.person_type}_${item.person_id}`;
                    const isPresent = presences[key] || false;
                    
                    return (
                      <tr key={key} className="hover:bg-gray-50">
                        <td className="px-4 sm:px-6 py-4">
                          <input
                            type="checkbox"
                            checked={isPresent}
                            onChange={() => handleTogglePresence(item.person_type, item.person_id)}
                            className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                          />
                        </td>
                        <td className="px-4 sm:px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">
                            {item.person_name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {item.person_email}
                          </div>
                        </td>
                        <td className="px-4 sm:px-6 py-4">
                          <span className="text-sm text-gray-900 capitalize">
                            {item.person_type === 'interviewer' ? 'Enquêteur' : 
                             item.person_type === 'controller' ? 'Contrôleur' : 'Prestataire'}
                          </span>
                        </td>
                        <td className="px-4 sm:px-6 py-4">
                          <div className="text-sm text-gray-900">
                            {item.assigned_at ? new Date(item.assigned_at).toLocaleDateString('fr-FR') : 'N/A'}
                          </div>
                        </td>
                        <td className="px-4 sm:px-6 py-4">
                          <div className="flex items-center">
                            <div className="w-24 bg-gray-200 rounded-full h-2 mr-2">
                              <div
                                className="bg-red-600 h-2 rounded-full"
                                style={{ width: `${item.progress_percent || 0}%` }}
                              ></div>
                            </div>
                            <span className="text-sm text-gray-700">{item.progress_percent || 0}%</span>
                          </div>
                        </td>
                        <td className="px-4 sm:px-6 py-4">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(item.status, item.display_status)}`}>
                            {getStatusText(item.status, item.display_status)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Checklist Cards - Mobile */}
          <div className="md:hidden space-y-3 mb-4 sm:mb-5 md:mb-6">
            {checklist.map((item) => {
              const key = `${item.person_type}_${item.person_id}`;
              const isPresent = presences[key] || false;
              
              return (
                <div key={key} className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <input
                          type="checkbox"
                          checked={isPresent}
                          onChange={() => handleTogglePresence(item.person_type, item.person_id)}
                          className="h-5 w-5 text-red-600 focus:ring-red-500 border-gray-300 rounded flex-shrink-0 mt-0.5"
                        />
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm sm:text-base font-semibold text-gray-900 break-words">
                            {item.person_name}
                          </h3>
                          <p className="text-xs text-gray-500 break-all">{item.person_email}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-2 pl-8">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-600">Type:</span>
                      <span className="text-xs sm:text-sm text-gray-900 capitalize font-medium">
                        {item.person_type === 'interviewer' ? 'Enquêteur' : 
                         item.person_type === 'controller' ? 'Contrôleur' : 'Prestataire'}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-600">Assigné le:</span>
                      <span className="text-xs sm:text-sm text-gray-900">
                        {item.assigned_at ? new Date(item.assigned_at).toLocaleDateString('fr-FR') : 'N/A'}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-600">Progression:</span>
                      <div className="flex items-center gap-2">
                        <div className="w-20 bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-red-600 h-2 rounded-full"
                            style={{ width: `${item.progress_percent || 0}%` }}
                          ></div>
                        </div>
                        <span className="text-xs sm:text-sm text-gray-700 font-medium">{item.progress_percent || 0}%</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-600">Statut:</span>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(item.status, item.display_status)}`}>
                        {getStatusText(item.status, item.display_status)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row justify-end gap-3 sm:gap-4">
            <button
              onClick={() => navigate(`/formations/${id}`)}
              className="w-full sm:w-auto px-4 sm:px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-sm sm:text-base"
            >
              Annuler
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="w-full sm:w-auto px-4 sm:px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-sm sm:text-base"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Enregistrement...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faSave} className="mr-2" />
                  Enregistrer
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PresenceChecklist;

